import { useEffect, useId, useRef, useState } from 'react';
import { Clock, X } from 'lucide-react';
import { DATE_RANGE_TEXT, parseTimeInput, plausibleDate } from '@kaba/shared';
import { cx } from '../ui/ui';

const inputCls =
  'min-h-11 rounded-lg border bg-[var(--panel)] px-2.5 text-base outline-none focus:border-accent sm:min-h-10 sm:text-sm';

/**
 * Datum mit optionaler Uhrzeit (Start, Frist). Ablauf: Datum wählen → „Uhrzeit hinzufügen“ →
 * beschriftetes Uhrzeitfeld (24 Stunden, „hh:mm“, 00:00 ist gültig) mit „Uhrzeit entfernen“.
 * Die Uhrzeit ist ein Textfeld statt `type=time`, damit sie unabhängig von der Spracheinstellung
 * des Browsers immer im 24-Stunden-Format erscheint.
 *
 * `onCommit` bekommt Datum („JJJJ-MM-TT“ oder leer) und Uhrzeit („HH:MM“ oder null) und gibt eine
 * Fehlermeldung zurück, wenn der Wert nicht übernommen wurde (sie erscheint am Feld).
 */
export function DateTimeField({
  id,
  label,
  date,
  time,
  disabled,
  error: outerError,
  onCommit,
  className,
}: {
  id?: string;
  label: string;
  date: string;
  time: string | null;
  disabled?: boolean;
  /** Fehler aus dem Zusammenspiel mit dem anderen Termin (z. B. vorhandene ungültige Daten) */
  error?: string | null;
  onCommit: (date: string, time: string | null) => string | null;
  className?: string;
}) {
  const uid = useId();
  const dateId = id ?? `${uid}-d`;
  const errId = `${uid}-e`;
  const hintId = `${uid}-h`;
  const [d, setD] = useState(date);
  const [t, setT] = useState(time ?? '');
  const [timeOpen, setTimeOpen] = useState(!!time);
  const [error, setError] = useState<string | null>(null);
  const timeRef = useRef<HTMLInputElement>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const focusTime = useRef(false);

  // Änderungen von außen (Speichern, andere Personen) übernehmen
  useEffect(() => {
    setD(date);
    setT(time ?? '');
    setTimeOpen(!!time);
    setError(null);
  }, [date, time]);

  useEffect(() => {
    if (timeOpen && focusTime.current) {
      focusTime.current = false;
      timeRef.current?.focus();
    }
  }, [timeOpen]);

  const commit = (nd: string, nt: string | null) => {
    const err = onCommit(nd, nd ? nt : null);
    setError(err);
    return !err;
  };

  const commitTime = () => {
    if (!t.trim()) return; // leer gelassen: noch nichts übernehmen
    const parsed = parseTimeInput(t);
    if (!parsed) {
      setError('Uhrzeit bitte als hh:mm im 24-Stunden-Format eingeben, z. B. 09:30 oder 00:00.');
      return;
    }
    setT(parsed);
    if (parsed !== time || d !== date) commit(d, parsed);
    else setError(null);
  };

  const shownError = error ?? outerError ?? null;
  const parsedOrNull = () => (timeOpen ? parseTimeInput(t) : null);

  return (
    <div className={cx('flex min-w-0 flex-col gap-1.5', className)} role="group" aria-labelledby={`${uid}-l`}>
      <label id={`${uid}-l`} htmlFor={dateId} className="text-xs font-semibold tracking-wide text-[var(--ink-soft)] uppercase">
        {label}
      </label>
      <div className="flex flex-wrap items-center gap-1.5">
        <input
          id={dateId}
          type="date"
          aria-label={`${label}: Datum`}
          aria-invalid={!!shownError || undefined}
          aria-describedby={shownError ? errId : undefined}
          className={cx(inputCls, 'min-w-0 flex-[1_1_9rem]', shownError ? 'border-rose-400' : 'border-[var(--line)]')}
          value={d}
          disabled={disabled}
          onChange={(e) => {
            const nd = e.target.value;
            setD(nd);
            // Beim Tippen des Jahres kommen Zwischenwerte wie „0002-…“ – nicht speichern
            if (nd && !plausibleDate(nd)) {
              setError(DATE_RANGE_TEXT);
              return;
            }
            if (!nd) {
              setTimeOpen(false);
              setT('');
            }
            commit(nd, parsedOrNull());
          }}
        />
        {d && !timeOpen && !disabled && (
          <button
            ref={addRef}
            type="button"
            onClick={() => {
              focusTime.current = true;
              setTimeOpen(true);
            }}
            className="inline-flex min-h-11 items-center gap-1.5 rounded-lg px-2 text-sm font-medium text-accent-fg hover:bg-accent-soft sm:min-h-9"
          >
            <Clock size={15} aria-hidden /> Uhrzeit hinzufügen
            <span className="text-xs font-normal text-[var(--ink-soft)]">(optional)</span>
          </button>
        )}
        {d && timeOpen && (
          <span className="flex items-center gap-1">
            <input
              ref={timeRef}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="hh:mm"
              maxLength={8}
              aria-label={`${label}: Uhrzeit`}
              aria-invalid={!!shownError || undefined}
              aria-describedby={shownError ? `${errId} ${hintId}` : hintId}
              className={cx(inputCls, 'w-[5.5rem] text-center tabular-nums', shownError ? 'border-rose-400' : 'border-[var(--line)]')}
              value={t}
              disabled={disabled}
              onChange={(e) => setT(e.target.value)}
              onBlur={commitTime}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitTime();
                }
              }}
            />
            <span id={hintId} className="sr-only">
              24-Stunden-Format, zum Beispiel 09:30
            </span>
            {!disabled && (
              <button
                type="button"
                onClick={() => {
                  setTimeOpen(false);
                  setT('');
                  if (time) commit(d, null);
                  else setError(null);
                  requestAnimationFrame(() => addRef.current?.focus());
                }}
                aria-label={`${label}: Uhrzeit entfernen`}
                title="Uhrzeit entfernen"
                className="inline-flex size-11 items-center justify-center rounded-lg text-[var(--ink-soft)] hover:bg-black/5 hover:text-[var(--ink)] sm:size-9 dark:hover:bg-white/10"
              >
                <X size={16} aria-hidden />
              </button>
            )}
          </span>
        )}
      </div>
      {shownError && (
        <p id={errId} className="text-sm text-rose-700 dark:text-rose-300" role="alert">
          {shownError}
        </p>
      )}
    </div>
  );
}
